import React from "react";
import { connect, type ConnectedProps } from "react-redux";
import { Card } from "react-bootstrap";
import { get, isEmpty, minBy } from "lodash";
import moment from "moment";

import IconTable, { type IconTableColumn } from "components/IconTable";
import { contributionAbilitySelector } from "store/selectors/contribution";
import { formatCurrency } from "utils/number";
import type { RootState } from "types/state";
import type { ContributionAbility } from "types/models";

// `types/models` re-exports the durable model surface but not the per-year
// sub-entity; derive it from the exported parent so the row shape stays tied
// to the schema (ContributionAbility.history is Array<ContributionAbilityByYear>).
type ContributionAbilityByYear = NonNullable<
  ContributionAbility["history"]
>[number];

const mapStateToProps = (state: RootState) => ({
  // contributionAbilitySelector reads the still-untyped user slice and returns
  // `unknown` — cast at the boundary to the entity this component consumes.
  contributionAbility: contributionAbilitySelector(state) as
    | ContributionAbility
    | undefined,
  iraAccounts: state.accounts.iraAccounts,
});

const connector = connect(mapStateToProps);

type Props = ConnectedProps<typeof connector>;

class ContributionHistory extends React.PureComponent<Props> {
  _getTableColumns = (): Array<IconTableColumn<ContributionAbilityByYear>> => {
    return [
      {
        key: "taxYear",
        label: "Year",
      },
      {
        key: "totalContributionAmount",
        label: "Amount",
        format: (value) => formatCurrency(value as number),
      },
    ];
  };

  _getFilteredHistory = (): ContributionAbilityByYear[] => {
    const history = get(this.props, "contributionAbility.history", []);

    const { iraAccounts, contributionAbility } = this.props;

    if (isEmpty(iraAccounts) || isEmpty(history)) {
      return [];
    }

    const earliestAccount = minBy(iraAccounts, (account) =>
      moment(account.createdAt).valueOf()
    );
    // minBy returns `IraAccount | undefined`; the earlier isEmpty(iraAccounts)
    // guard guarantees a defined element at runtime (TS can't narrow it).
    const earliestAccountYear = moment(earliestAccount!.createdAt).year();
    const currentYear = moment().year();
    const isWithinPriorYearContributionWindow =
      contributionAbility?.isWithinPriorYearContributionWindow;

    let earliestYear = earliestAccountYear;
    if (
      earliestAccountYear === currentYear &&
      isWithinPriorYearContributionWindow
    ) {
      earliestYear = earliestAccountYear - 1;
    }

    return history.filter((entry) => entry.taxYear >= earliestYear);
  };

  render() {
    const filteredHistory = this._getFilteredHistory();

    if (isEmpty(filteredHistory)) {
      return null;
    }

    return (
      <Card className="dashboard-widget">
        <Card.Header>
          <h5>Contribution History</h5>
        </Card.Header>
        <Card.Body>
          <IconTable columns={this._getTableColumns()} data={filteredHistory} />
        </Card.Body>
      </Card>
    );
  }
}

export default connector(ContributionHistory);
